import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { createPortalApi } from '../../api/portalApi';
import { useRuntimeConfig } from '../../context/RuntimeConfigContext';
import { useToast } from '../../components/Toast';
import Field from '../../components/settings/Field';
import { PROVIDER_MODELS } from '../../constants/llmProviders';
import type { UserSettings } from '../../types';

export default function MyLlmSettingsPage() {
  const { instance } = useMsal();
  const cfg           = useRuntimeConfig();
  const api            = createPortalApi(instance, cfg.msalClientId, cfg.apiBaseUrl);
  const { showToast }  = useToast();

  const [userSettings, setUserSettings] = useState<UserSettings>({});
  const [loading, setLoading]           = useState(true);
  const [saving, setSaving]             = useState(false);
  const [error, setError]               = useState('');

  useEffect(() => {
    api.getUserSettings()
      .then(u => setUserSettings(u ?? {}))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    try {
      await api.saveUserSettings(userSettings);
      showToast('Settings saved successfully.', 'success');
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div className="page-loading"><span className="spinner" />Loading settings…</div>;

  return (
    <div>
      <Link to="/settings/profile" className="settings-back-link">← My Settings</Link>

      {error && <div className="alert alert-error">{error}</div>}

      <form className="settings-form" onSubmit={handleSubmit}>
        <div className="settings-card">
          <h2 className="settings-section-title">AI / LLM Configuration</h2>
          <p className="settings-section-desc">
            Configure your LLM provider and key. Both the portal Generator and the Chrome
            extension use these settings. Keys are stored encrypted and never exposed to
            the browser. If no personal key is set, the organisation's shared key is used
            automatically.
          </p>

          {/* Key-source toggle — only shown when user has a personal key */}
          {userSettings.llmApiKey && (
            <div className="form-group mb-sm">
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                Which API key should be used for AI requests?
              </label>
              <div className="stack-gap-sm">
                <label className="row-flex" style={{ cursor: 'pointer', fontWeight: 'normal' }}>
                  <input
                    type="radio"
                    name="llmKeySource"
                    checked={userSettings.llmUseOrgKey === true}
                    onChange={() => setUserSettings(u => ({ ...u, llmUseOrgKey: true }))}
                  />
                  Use the organisation's shared key (set by your admin)
                </label>
                <label className="row-flex" style={{ cursor: 'pointer', fontWeight: 'normal' }}>
                  <input
                    type="radio"
                    name="llmKeySource"
                    checked={userSettings.llmUseOrgKey !== true}
                    onChange={() => setUserSettings(u => ({ ...u, llmUseOrgKey: false }))}
                  />
                  Use my personal API key (configured below)
                </label>
              </div>
            </div>
          )}

          <div className="form-grid">
            <div className="form-group">
              <label>LLM Provider</label>
              <select
                className="form-input"
                value={userSettings.llmProvider ?? 'claude'}
                onChange={e => {
                  const provider = e.target.value;
                  const firstModel = PROVIDER_MODELS[provider]?.[0]?.value ?? '';
                  setUserSettings(u => ({ ...u, llmProvider: provider, llmModel: firstModel }));
                }}
              >
                <option value="claude">Claude (Anthropic)</option>
                <option value="openai">OpenAI</option>
                <option value="groq">Groq</option>
                <option value="azureopenai">Azure OpenAI</option>
              </select>
            </div>
            <div className="form-group">
              <label>Model</label>
              <select
                className="form-input"
                value={userSettings.llmModel ?? PROVIDER_MODELS[userSettings.llmProvider ?? 'claude']?.[0]?.value ?? ''}
                onChange={e => setUserSettings(u => ({ ...u, llmModel: e.target.value }))}
              >
                {(PROVIDER_MODELS[userSettings.llmProvider ?? 'claude'] ?? []).map(m => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </div>
            {(userSettings.llmProvider ?? 'claude') === 'azureopenai' && (
              <Field
                label="API Endpoint"
                placeholder="https://<resource>.openai.azure.com/openai/deployments/<deployment>/chat/completions?api-version=2024-02-15-preview"
                value={userSettings.llmApiEndpoint ?? ''}
                onChange={v => setUserSettings(u => ({ ...u, llmApiEndpoint: v }))}
              />
            )}
            <Field
              label="API Key"
              placeholder="Your personal API key (leave blank to always use the org key)"
              value={userSettings.llmApiKey ?? ''}
              onChange={v => setUserSettings(u => ({ ...u, llmApiKey: v }))}
              secret
            />
          </div>
        </div>

        <div className="settings-footer">
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Saving…' : 'Save Settings'}
          </button>
        </div>
      </form>
    </div>
  );
}
